/** API canónica de kits bajo rincón (KBR*) — ya no se usa para forzar GLB. */
export const TPC_PLANNER_API_BASE = 'https://planificador3dapi.cocinas-tpc.com'

/** idUnidades resueltos vía API TPC (legado; ya no redirige peticiones). */
const tpcResolvedUnitIds = new Set<number>()

/**
 * Normaliza un código de catálogo quitando prefijo PE y sufijos de acabado/gola.
 * Ej: "PE KBRB8010045 Li GO" → "KBRB8010045"
 */
export function normalizeCatalogCode(catalogID: string | number | null | undefined): string {
  let code = String(catalogID ?? '')
    .trim()
    .toUpperCase()
  if (!code) return ''
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  code = code.replace(/\s+(LI|LA|RU|R2)(\s+GO)?$/i, '').replace(/\s+GO$/i, '').trim()
  return code
}

/** Unidades cuyo código (normalizado) empieza por KBR. */
export function isKBRCatalog(catalogID: string | number | null | undefined): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KBR')
}

/** KARB* (no usar includes('arb'): KBRB también lo contiene). */
export function isKARBCatalog(catalogID: string | number | null | undefined): boolean {
  return normalizeCatalogCode(catalogID).startsWith('KARB')
}

/**
 * Regleta para geometría/snap = valor sidebar/API.
 * KBR/KARB: directa (20→20). Antes KBR invertía max+min−v (20↔120).
 */
export function getRegletaEffectiveMm(
  item: {
    regleta?: number | null
    catalogID?: string | number
    morphs?: Array<{ name: string; min: number; max: number }>
  } | null | undefined,
): number {
  const stored = Number(item?.regleta ?? 0)
  if (!Number.isFinite(stored) || stored <= 0) return 0
  if (isKBRCatalog(item?.catalogID) || isKARBCatalog(item?.catalogID)) return stored
  const morph = item?.morphs?.find((m) => m.name === 'Regleta')
  if (!morph || !(Number(morph.max) > Number(morph.min))) return stored
  if (stored >= morph.max) return morph.min
  if (stored <= morph.min) return morph.max
  return morph.max + morph.min - stored
}

/** Proyectos antiguos: la regleta KBR se interpreta 20 mm más corta de lo actual. */
export const KBR_LEGACY_REGLETA_EXTRA_MM = 20

type KbrRegletaItem = {
  catalogID?: string | number
  codigo?: string | number
  referencia?: string | number
  regleta?: number | null
  regletaMinimo?: number | null
  regletaMaximo?: number | null
  kbrLegacyRegletaPlus20Applied?: boolean
  morphs?: Array<{
    name?: string
    default?: number
    _default?: number
    min?: number
    max?: number
  }>
}

/**
 * Al abrir un JSON antiguo: 60 → 80 (y cualquier valor +20, clamp al max API).
 * Idempotente (`kbrLegacyRegletaPlus20Applied`). No aplicar al dibujar unidades nuevas.
 */
export function applyKbrLegacyRegletaPlus20(item: KbrRegletaItem | null | undefined): boolean {
  if (!item || item.kbrLegacyRegletaPlus20Applied) return false
  if (
    !isKBRCatalog(item.catalogID) &&
    !isKBRCatalog(item.codigo) &&
    !isKBRCatalog(item.referencia)
  ) {
    return false
  }
  const morph = item.morphs?.find((m) => m.name === 'Regleta')
  const min = Number(item.regletaMinimo ?? morph?.min ?? 20)
  const max = Number(item.regletaMaximo ?? morph?.max ?? 120)
  const current = Number(item.regleta ?? morph?.default ?? 0)
  item.kbrLegacyRegletaPlus20Applied = true
  if (!(current > 0) || !Number.isFinite(current)) return false
  const lo = Number.isFinite(min) && min > 0 ? min : 20
  const hi = Number.isFinite(max) && max > lo ? max : 120
  const next = Math.max(lo, Math.min(hi, current + KBR_LEGACY_REGLETA_EXTRA_MM))
  item.regleta = next
  if (morph) {
    morph.default = next
    morph._default = next
  }
  return next !== current
}

export function rememberTpcUnitId(idUnidades: number | null | undefined) {
  const id = Number(idUnidades)
  if (Number.isFinite(id) && id > 0) tpcResolvedUnitIds.add(id)
}

export function isTpcResolvedUnitId(idUnidades: number | null | undefined): boolean {
  const id = Number(idUnidades)
  return Number.isFinite(id) && id > 0 && tpcResolvedUnitIds.has(id)
}

/**
 * Ya no se fuerza planificador3dapi para KBR: el GLB/unidad sale de la API
 * normal del proyecto (getProxyUrl). Las tiras Zocalo 4000 van aparte.
 */
export function resolveUnitApiBaseUrl(
  _catalogID?: string | number | null,
  _idUnidades?: number | null,
): string | null {
  return null
}

/**
 * Antes KBR omitía Zocalo 4000. Ahora KBR usa tira (recta / L frente+retorno).
 */
export function shouldSkipAutoZocalo(_catalogID: string | number | null | undefined): boolean {
  return false
}

/** @deprecated No reescribe a planner; se mantiene por compat. */
export function rewriteToPlannerCatalogUrl(url?: string | null): string | undefined {
  return url ?? undefined
}

/** @deprecated Identity: no reescribir URLs de CatalogData a planner. */
export function rewriteKbrUnitPayloadUrls<T extends Record<string, any>>(json: T): T {
  return json
}
